import {Plural, Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VirtualTableMultiSelectStatesEnum} from '../../components/common/table/helpers/tableHelpers';
import {TableMultiSelectHeaderRow} from '../../components/common/table/tableMultiSelectHeaderRow';

/*
 * Props.
 */

interface SettingsAnswersMultiSelectHeaderProps {
  selectedIds: ReadonlyArray<string>;
  totalItems: number;
  onMultiSelectChange: HandlerOf<boolean>;
  onDeleteClick: Handler;
  onMoveClick: Handler;
}

/*
 * Style.
 */

const StyledButtonsWrapper = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

/*
 * Component.
 */

export const SettingsAnswersMultiSelectHeader: FC<SettingsAnswersMultiSelectHeaderProps> = (props) => {
  const {selectedIds, totalItems, onMultiSelectChange} = props;

  const foldersSelected = useMemo(() => selectedIds.filter((id) => id.startsWith('folder:')), [selectedIds]);
  const answersSelected = useMemo(() => selectedIds.filter((id) => id.startsWith('answer:')), [selectedIds]);

  const hasSomeSelected = useMemo(() => {
    if (!selectedIds.length) {
      return false;
    }

    return totalItems > selectedIds.length;
  }, [selectedIds.length, totalItems]);

  const hasAllSelected = useMemo(() => selectedIds.length === totalItems, [selectedIds.length, totalItems]);

  const multiSelectCheckboxState: VirtualTableMultiSelectStatesEnum = useMemo(() => {
    if (hasSomeSelected) {
      return VirtualTableMultiSelectStatesEnum.INDETERMINATE;
    }

    return hasAllSelected
      ? VirtualTableMultiSelectStatesEnum.CHECKED
      : VirtualTableMultiSelectStatesEnum.UNCHECKED;
  }, [hasAllSelected, hasSomeSelected]);

  const renderTotalItemsLabel = () => {
    const folderPlural = (
      <Plural
        id="settings-answers-selectedfolders"
        value={foldersSelected.length}
        zero="No folders selected"
        one="1 folder selected"
        other="# folders selected"
      />
    );

    const answerPlural = (
      <Plural
        id="settings-answers-selectedanswers"
        value={answersSelected.length}
        zero="No templates selected"
        one="1 template selected"
        other="# templates selected"
      />
    );

    if (foldersSelected.length > 0 && answersSelected.length === 0) {
      return folderPlural;
    }

    if (foldersSelected.length === 0 && answersSelected.length > 0) {
      return answerPlural;
    }

    return (
      <Trans id="settings-answers-selecteditems">
        {folderPlural} and {answerPlural}
      </Trans>
    );
  };

  return (
    <TableMultiSelectHeaderRow
      multiSelectStatus={multiSelectCheckboxState}
      onMultiSelectChange={onMultiSelectChange}
      renderTotalItemsLabel={renderTotalItemsLabel}
    >
      <StyledButtonsWrapper>
        <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={props.onDeleteClick}>
          <Trans id="settingstags-bulk-delete">Delete</Trans>
        </Button>
        <Button onClick={props.onMoveClick}>
          <Trans id="settingstags-bulk-move">Move</Trans>
        </Button>
      </StyledButtonsWrapper>
    </TableMultiSelectHeaderRow>
  );
};
